import React, { useState } from "react";
import { Loader2, ShieldCheck, X, AlertTriangle } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useSaveConsents } from "../../hooks/queries/useAthletes";
import { DOCUMENT_VERSION } from "../../data/legalDocuments";
import ExpandableText from "./ExpandableText";

/* ────────────────────────────────────────────────────────────
   Authorizations config
   ──────────────────────────────────────────────────────────── */
const ADULT_AUTHORIZATIONS = [
  {
    key: "general_data",
    label: "Autorizo el tratamiento de mis datos personales conforme a la Política de ATHLETAIN.",
    description:
      "Incluye nombre, correo electrónico, documento de identidad, información deportiva, historial de rendimiento y demás datos necesarios para la operación de la plataforma, comunicaciones del servicio y mejora continua de las funcionalidades.",
  },
  {
    key: "sensitive_data",
    label: "Autorizo el tratamiento de mis datos sensibles.",
    description:
      "Comprende datos de salud, condición física, lesiones, biometría y rendimiento deportivo. Su tratamiento se realiza exclusivamente para los fines deportivos de la plataforma, conforme al artículo 6 de la Ley 1581 de 2012. Esta autorización es voluntaria.",
  },
  {
    key: "international_transfer",
    label: "Autorizo la transferencia internacional de mis datos.",
    description:
      "Mis datos podrán ser almacenados o procesados en servidores ubicados fuera de Colombia (incluyendo proveedores como AWS o Google Cloud) bajo las garantías y estándares de seguridad descritos en la Política de Tratamiento de Datos de ATHLETAIN.",
  },
  {
    key: "image_voice",
    label: "Autorizo el uso de mi imagen, voz y contenido audiovisual en ATHLETAIN.",
    description:
      "Autorizo el uso de fotografías, videos y grabaciones de voz generados dentro de la plataforma para la operación del servicio, construcción de mi perfil deportivo y, de ser el caso, difusión con fines de visibilidad deportiva, conforme a los Términos y Condiciones.",
  },
];

/* ────────────────────────────────────────────────────────────
   Error popup (reused pattern)
   ──────────────────────────────────────────────────────────── */
const ErrorPopup = ({ isOpen, message, onClose }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-8 relative"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
          >
            <X size={20} />
          </button>
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertTriangle size={28} className="text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Algo salió mal
            </h3>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">{message}</p>
            <button
              onClick={onClose}
              className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3 rounded-lg transition-colors duration-200"
            >
              Entendido
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

/* ────────────────────────────────────────────────────────────
   ConsentPage
   Props:
     athleteId  — string (UUID from athletes table)
     isMinor    — boolean
     onComplete — () => void
   ──────────────────────────────────────────────────────────── */
const ConsentPage = ({ athleteId, isMinor, onComplete }) => {
  const authorizations = ADULT_AUTHORIZATIONS;
  const [checked, setChecked] = useState(
    Object.fromEntries(authorizations.map((a) => [a.key, false]))
  );

  const [error, setError] = useState("");
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const saveConsents = useSaveConsents();
  const isLoading = saveConsents.isPending;

  const allChecked = Object.values(checked).every(Boolean);

  const handleToggle = (key) => {
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleComplete = async () => {
    if (isMinor || !allChecked || isLoading) return;

    const now = new Date().toISOString();
    const enhanced = {
      general_data_accepted_at: now,
      sensitive_data_accepted_at: now,
      image_voice_accepted_at: now,
      international_transfer_accepted_at: now,
      doc_version: DOCUMENT_VERSION,
    };

    // Server-side jsonb merge into attributes.consents — no read-merge-write.
    const consents = {
      enhanced,
      is_minor: isMinor,
    };

    try {
      await saveConsents.mutateAsync({ athleteId, consents });
      onComplete();
    } catch (err) {
      setError(
        "No pudimos guardar tus autorizaciones. Por favor inténtalo de nuevo."
      );
      setShowErrorPopup(true);
    }
  };

  // An athlete session cannot attest to being its own legal representative.
  // Guardian authorization must come from the server's verified guardian flow.
  if (isMinor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-4 py-8">
        <div className="w-full max-w-lg space-y-4 text-center">
          <ShieldCheck size={32} className="mx-auto text-blue-950" />
          <h1 className="text-2xl font-bold text-gray-900">Autorización del tutor pendiente</h1>
          <p className="text-base text-gray-700">
            Tu registro necesita la autorización verificada de tu padre, madre o representante legal.
            No puedes otorgarla desde tu cuenta de deportista.
          </p>
          <p className="text-base text-gray-700">
            Indicar el nombre y correo de tu tutor no confirma esa autorización.
            Este paso no puede completarse desde esta pantalla.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-hidden">
      <div className="flex-1 flex items-start sm:items-center justify-center px-4 py-8 sm:py-12">
        <div className="w-full max-w-lg">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
              <ShieldCheck size={28} className="text-blue-950" />
            </div>
          </div>

          <AnimatePresence mode="wait">
              <motion.div
                key="authorizations"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
                className="space-y-5"
              >
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-gray-900 uppercase tracking-wide italic mb-2">
                    Autorizaciones de datos
                  </h1>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Para activar tu perfil necesitamos las siguientes autorizaciones. Cada una es obligatoria.
                  </p>
                </div>

                <div className="space-y-3">
                  {authorizations.map((auth) => (
                    <div
                      key={auth.key}
                      className={`border rounded-xl p-4 transition-colors ${
                        checked[auth.key]
                          ? "border-blue-900 bg-blue-50"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <label className="flex items-start gap-3 cursor-pointer select-none w-full touch-manipulation">
                        <input
                          type="checkbox"
                          checked={checked[auth.key]}
                          onChange={() => handleToggle(auth.key)}
                          className="w-4 h-4 shrink-0 mt-1 rounded border-gray-300 accent-blue-950 focus:ring-blue-900 cursor-pointer"
                        />
                        <div>
                          <p className="text-sm font-semibold text-gray-900 mb-1">
                            {auth.label}
                          </p>
                          <ExpandableText
                            text={auth.description}
                            className="text-xs text-gray-600 leading-relaxed"
                          />
                        </div>
                      </label>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-gray-400 text-center leading-relaxed px-2">
                  Estas autorizaciones son requeridas para usar la plataforma. Puedes revocarlas en cualquier momento escribiendo a datos@athletain.com.
                </p>

                <div className="flex justify-center pt-2">
                  <button
                    onClick={handleComplete}
                    disabled={!allChecked || isLoading}
                    className="bg-blue-950 hover:bg-blue-900 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200 flex items-center gap-3"
                  >
                    {isLoading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <>
                        Completar registro
                        <span className="text-base">✦</span>
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <ErrorPopup
        isOpen={showErrorPopup}
        message={error}
        onClose={() => setShowErrorPopup(false)}
      />
    </div>
  );
};

export default ConsentPage;
